import React, { useEffect, useState, useCallback } from 'react';
import { range12 } from '../lib/timeFormat';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ClipboardCheck, Video, Clock, Check, X, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'TBD';

export default function InductionApprovalsPage() {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState({}); // key -> 'reject'|'notconducted'
  const [reason, setReason] = useState({});
  const [suggested, setSuggested] = useState({});
  const [ncReason, setNcReason] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/inductions/owner/pending`, { headers: getAuthHeader() });
    if (r.ok) setRows(await r.json());
    setLoading(false);
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const respond = async (row, action) => {
    const key = `${row.induction_id}:${row.session_id}`;
    if (action === 'reject' && !(reason[key] || '').trim()) { toast.error('Please provide a reason'); return; }
    if (action === 'reschedule' && !(suggested[key] || '').trim()) { toast.error('Please suggest a time'); return; }
    const r = await fetch(`${API}/inductions/${row.induction_id}/sessions/${row.session_id}/respond`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, reason: reason[key] || '', suggested_time: suggested[key] || '' }),
    });
    if (r.ok) { toast.success('Response recorded'); setMode({ ...mode, [key]: null }); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };

  const confirmConduct = async (row, status) => {
    const key = `${row.induction_id}:${row.session_id}`;
    const reasonTxt = ncReason[key] || '';
    if (status === 'Not Conducted' && !reasonTxt.trim()) { toast.error('Please enter the reason the session was not conducted'); return; }
    const r = await fetch(`${API}/inductions/${row.induction_id}/sessions/${row.session_id}/conduct`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, reason: reasonTxt }),
    });
    if (r.ok) { toast.success(status === 'Conducted' ? 'Confirmed — feedback released to joiners' : 'Marked Not Conducted'); setMode({ ...mode, [key]: null }); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="induction-approvals-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><ClipboardCheck className="h-6 w-6" /> Induction Approvals</h1>
          <p className="text-muted-foreground">Confirm the induction sessions you've been assigned to lead.</p>
        </div>

        {loading ? <p className="text-muted-foreground">Loading...</p>
          : rows.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground" data-testid="approvals-empty">
              <ClipboardCheck className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No sessions awaiting your confirmation.</p>
            </CardContent></Card>
          ) : rows.map((row) => {
            const key = `${row.induction_id}:${row.session_id}`;
            const m = mode[key];
            return (
              <Card key={key} data-testid={`approval-card-${row.session_id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base">{row.name}</CardTitle>
                    <Badge variant="outline">{row.approval_status}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{row.approval_status === 'Accepted' ? (row.completed ? 'This session is completed.' : "You've accepted this session — details below.") : row.approval_status === 'Rejected' ? 'You rejected this session.' : 'Confirm the timing for your session below.'}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /> {fmtDate(row.date)} {row.start_time && `· ${range12(row.start_time, row.end_time)}`}</div>
                    {row.online_url && <div className="flex items-center gap-2"><Video className="h-4 w-4 text-muted-foreground" /> <a href={row.online_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" data-testid={`meeting-link-${row.session_id}`}>Meeting link</a></div>}
                    {row.suggested_time && <div className="sm:col-span-2 text-amber-600">Your suggested time: {row.suggested_time}</div>}
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Joiners ({(row.joiners || []).length})</p>
                    <div className="flex flex-wrap gap-2" data-testid={`joiners-${row.session_id}`}>
                      {(row.joiners || []).map(j => (
                        <Badge key={j.employee_id} variant="secondary" className="py-1">
                          {j.name} · <span className={j.mode === 'Online' ? 'text-blue-600 ml-1' : 'text-slate-500 ml-1'}>{j.mode}</span>{j.mode === 'Offline' && j.location ? ` (${j.location})` : ''}
                        </Badge>
                      ))}
                      {(row.joiners || []).length === 0 && <span className="text-sm text-muted-foreground">No joiners assigned.</span>}
                    </div>
                  </div>

                  {(row.same_day_sessions || []).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Also booked on {fmtDate(row.date)}</p>
                      <div className="space-y-1" data-testid={`sameday-${row.session_id}`}>
                        {(row.same_day_sessions || []).map((o, i) => (
                          <div key={i} className="text-sm text-muted-foreground flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {o.start_time ? range12(o.start_time, o.end_time) : '--:--'} · {o.name}</div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Use this to suggest an alternative time if the slot clashes.</p>
                    </div>
                  )}

                  {row.approval_status === 'Rejected' ? (
                    <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 text-sm p-3" data-testid={`rejected-banner-${row.session_id}`}>You rejected this session{row.suggested_time ? ` and suggested ${row.suggested_time}` : ''}. HR has been notified and may reschedule it.</div>
                  ) : row.approval_status === 'Accepted' ? (
                    row.completed ? (
                      <div className="rounded-lg bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 text-sm p-3 flex items-center gap-2" data-testid={`completed-banner-${row.session_id}`}><Check className="h-4 w-4" /> This session is completed — all joiners submitted their feedback.</div>
                    ) : row.conduct_status === 'Conducted' ? (
                      <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-sm p-3 flex items-center gap-2" data-testid={`conducted-banner-${row.session_id}`}><Check className="h-4 w-4" /> You confirmed this session was conducted — feedback is open to joiners.</div>
                    ) : row.conduct_status === 'Not Conducted' ? (
                      <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 text-sm p-3" data-testid={`notconducted-banner-${row.session_id}`}>Marked <b>Not Conducted</b>{row.not_conducted_reason ? ` — ${row.not_conducted_reason}` : ''}.</div>
                    ) : row.awaiting_conduct ? (
                      mode[key] === 'notconducted' ? (
                        <div className="space-y-2 rounded-lg border p-3" data-testid={`conduct-form-${row.session_id}`}>
                          <Label>Reason Session Was Not Conducted *</Label>
                          <Textarea value={ncReason[key] || ''} onChange={e => setNcReason({ ...ncReason, [key]: e.target.value })} data-testid={`nc-reason-${row.session_id}`} placeholder="Why did the session not take place?" />
                          <div className="flex gap-2"><Button variant="destructive" onClick={() => confirmConduct(row, 'Not Conducted')} data-testid={`submit-notconducted-${row.session_id}`}>Confirm Not Conducted</Button><Button variant="ghost" onClick={() => setMode({ ...mode, [key]: null })}>Cancel</Button></div>
                        </div>
                      ) : (
                        <div className="rounded-lg border p-3 space-y-2" data-testid={`conduct-prompt-${row.session_id}`}>
                          <p className="text-sm font-medium">Confirm Session Status</p>
                          <p className="text-xs text-muted-foreground">This session has ended. Did it take place?</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => confirmConduct(row, 'Conducted')} data-testid={`session-conducted-${row.session_id}`}><Check className="h-4 w-4 mr-1" /> Session Conducted</Button>
                            <Button variant="outline" onClick={() => setMode({ ...mode, [key]: 'notconducted' })} data-testid={`session-notconducted-${row.session_id}`}><X className="h-4 w-4 mr-1" /> Session Not Conducted</Button>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-sm p-3 flex items-center gap-2" data-testid={`accepted-banner-${row.session_id}`}><Check className="h-4 w-4" /> You've accepted this session.</div>
                    )
                  ) : m === 'reject' ? (
                    <div className="space-y-2" data-testid={`reject-form-${row.session_id}`}>
                      <Label>Reason for rejection *</Label>
                      <Textarea value={reason[key] || ''} onChange={e => setReason({ ...reason, [key]: e.target.value })} data-testid={`reject-reason-${row.session_id}`} placeholder="Why can't you take this session?" />
                      <Label>Suggested time (optional)</Label>
                      <Input type="datetime-local" value={suggested[key] || ''} onChange={e => setSuggested({ ...suggested, [key]: e.target.value })} data-testid={`reject-suggest-${row.session_id}`} />
                      <div className="flex gap-2"><Button variant="destructive" onClick={() => respond(row, 'reject')} data-testid={`submit-reject-${row.session_id}`}>Submit Rejection</Button><Button variant="ghost" onClick={() => setMode({ ...mode, [key]: null })}>Cancel</Button></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => respond(row, 'accept')} data-testid={`accept-${row.session_id}`}><Check className="h-4 w-4 mr-1" /> Accept</Button>
                      <Button variant="outline" onClick={() => setMode({ ...mode, [key]: 'reject' })} data-testid={`reject-${row.session_id}`}><X className="h-4 w-4 mr-1" /> Reject</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
      </div>
    </Layout>
  );
}
